package com.schoolconnect.app.feature.timetable

import com.schoolconnect.app.datasource.network.response.TimetableClassHeaderResponse
import com.schoolconnect.app.feature.common.PaginationPolicy
import com.schoolconnect.app.repository.timetable.TimetableRepository
import com.schoolconnect.app.repository.classsection.ClassSectionRepository
import com.schoolconnect.app.repository.mystudents.TeacherMyStudentsRepository
import com.schoolconnect.app.repository.classsubjectlink.ClassSubjectLinkRepository
import com.schoolconnect.app.repository.academicyear.AcademicYearRepository
import com.schoolconnect.app.datasource.network.request.TimetableCombinedCreateRequest
import com.schoolconnect.app.designsystem.AppToastCenter
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.Job
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.cancel
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

class TimeTableViewModel(
    private val schoolId: String,
    private val assignedOnly: Boolean = false
) {
    private val scope = CoroutineScope(SupervisorJob() + Dispatchers.Default)
    private var loadJob: Job? = null
    private var viewJob: Job? = null
    private var subjectsJob: Job? = null
    private var editorTimetableJob: Job? = null
    private val _uiState = MutableStateFlow(TimeTableUiState(isLoading = true))
    val uiState: StateFlow<TimeTableUiState> = _uiState.asStateFlow()

    init {
        loadAcademicYear()
        if (!assignedOnly) load()
        loadClassSections()
    }

    private fun loadAcademicYear() {
        val normalizedSchoolId = schoolId.trim()
        if (normalizedSchoolId.isBlank()) return
        _uiState.update { it.copy(isAcademicYearLoading = true, academicYearError = "") }
        scope.launch {
            AcademicYearRepository.loadCurrentAcademicYear(normalizedSchoolId).fold(
                onSuccess = { year ->
                    val startDate = year?.startDate?.trim().orEmpty()
                    val endDate = year?.endDate?.trim().orEmpty()
                    _uiState.update {
                        it.copy(
                            academicYearStartDate = startDate,
                            academicYearEndDate = endDate,
                            isAcademicYearLoading = false,
                            academicYearError = if (startDate.isBlank() || endDate.isBlank()) "The current academic year has no valid dates." else ""
                        )
                    }
                },
                onFailure = { error ->
                    _uiState.update {
                        it.copy(
                            isAcademicYearLoading = false,
                            academicYearError = error.message ?: "Unable to load the current academic year."
                        )
                    }
                }
            )
        }
    }

    private fun loadClassSections() {
        scope.launch {
            if (assignedOnly) {
                TeacherMyStudentsRepository.loadDashboardStatistics().fold(
                    onSuccess = { statistics ->
                        val assignedSections = statistics.assignedClasses.mapNotNull { section ->
                            val id = section.classSectionId?.trim().orEmpty()
                            if (id.isBlank()) null else TimetableClassSectionOption(
                                id = id,
                                className = section.className.orEmpty().trim(),
                                sectionName = section.sectionName.orEmpty().trim()
                            )
                        }.distinctBy { it.id }.sortedWith { left, right ->
                            compareTimetableClassSections(
                                left.className,
                                left.sectionName,
                                right.className,
                                right.sectionName
                            )
                        }
                        _uiState.update { it.copy(classSections = assignedSections, errorMessage = "") }
                        load()
                    },
                    onFailure = { error ->
                        _uiState.update { it.copy(isLoading = false, classSections = emptyList(), rows = emptyList(), totalCount = 0, errorMessage = error.message ?: "Unable to load assigned classes.") }
                    }
                )
                return@launch
            }
            ClassSectionRepository.loadSchoolClassSectionLookup(schoolId).onSuccess { sections ->
                val orderedSections = sections.mapNotNull { section ->
                    val id = section.id?.takeIf { it.isNotBlank() } ?: return@mapNotNull null
                    TimetableClassSectionOption(id, section.className.orEmpty(), section.sectionName.orEmpty())
                }.sortedWith { left, right ->
                    compareTimetableClassSections(
                        left.className,
                        left.sectionName,
                        right.className,
                        right.sectionName
                    )
                }
                _uiState.update { state -> state.copy(classSections = orderedSections) }
                load()
            }
        }
    }

    fun onEvent(event: TimeTableUiEvent) {
        when (event) {
            is TimeTableUiEvent.TimetableSaved -> save(event.headerId, event.request, event.onSuccess)
            is TimeTableUiEvent.ViewRequested -> loadView(event.row)
            is TimeTableUiEvent.ClassSectionSelected -> {
                loadTimetableSubjects(event.classSectionId)
                loadEditorTimetable(event.classSectionId)
            }
            TimeTableUiEvent.ViewDismissed -> _uiState.update { it.copy(viewDetails = null, isViewLoading = false, viewErrorMessage = "") }
            is TimeTableUiEvent.SearchChanged -> updateAndLoad { copy(searchQuery = event.value, pageIndex = 0) }
            is TimeTableUiEvent.ClassFilterChanged -> updateAndLoad { copy(classFilter = event.value, pageIndex = 0) }
            is TimeTableUiEvent.StatusFilterChanged -> updateAndLoad { copy(statusFilter = event.value, pageIndex = 0) }
            is TimeTableUiEvent.PageChanged -> updateAndLoad { copy(pageIndex = event.value) }
            is TimeTableUiEvent.PageSizeChanged -> updateAndLoad { copy(pageSize = event.value, pageIndex = 0) }
            is TimeTableUiEvent.SortChanged -> updateAndLoad {
                if (sortBy == event.value) copy(sortAscending = !sortAscending, pageIndex = 0)
                else copy(sortBy = event.value, sortAscending = true, pageIndex = 0)
            }
            is TimeTableUiEvent.RowActionsToggled -> _uiState.update { state -> state.copy(selectedRowId = if (state.selectedRowId == event.id) null else event.id) }
            TimeTableUiEvent.FiltersCleared -> updateAndLoad { copy(searchQuery = "", classFilter = "", statusFilter = "", pageIndex = 0) }
        }
    }

    private fun loadTimetableSubjects(classSectionId: String) {
        val normalizedId = classSectionId.trim()
        subjectsJob?.cancel()
        if (normalizedId.isBlank()) {
            _uiState.update { it.copy(timetableSubjects = emptyList(), areTimetableSubjectsLoading = false, timetableSubjectsError = "", timetableSubjectsClassSectionId = "") }
            return
        }
        _uiState.update {
            it.copy(
                timetableSubjects = emptyList(),
                areTimetableSubjectsLoading = true,
                timetableSubjectsError = "",
                timetableSubjectsClassSectionId = normalizedId
            )
        }
        subjectsJob = scope.launch {
            ClassSubjectLinkRepository.loadClassSectionSubjectNames(schoolId, normalizedId).fold(
                onSuccess = { subjects ->
                    _uiState.update { state ->
                        if (state.timetableSubjectsClassSectionId == normalizedId) {
                            state.copy(timetableSubjects = subjects, areTimetableSubjectsLoading = false)
                        } else state
                    }
                },
                onFailure = { error ->
                    _uiState.update { state ->
                        if (state.timetableSubjectsClassSectionId == normalizedId) {
                            state.copy(areTimetableSubjectsLoading = false, timetableSubjectsError = error.message ?: "Unable to load subjects for this class.")
                        } else state
                    }
                }
            )
        }
    }

    private fun loadEditorTimetable(classSectionId: String) {
        val normalizedId = classSectionId.trim()
        editorTimetableJob?.cancel()
        _uiState.update {
            it.copy(
                editorTimetableClassSectionId = normalizedId,
                editorTimetableHeaderId = null,
                editorTimetable = null,
                isEditorTimetableLoading = normalizedId.isNotBlank(),
                editorTimetableError = "",
                editorSaveError = ""
            )
        }
        if (normalizedId.isBlank()) return
        editorTimetableJob = scope.launch {
            TimetableRepository.loadAll(refreshScope = scope).fold(
                onSuccess = { headers ->
                    val headerId = headers.firstOrNull { it.classSectionId?.trim() == normalizedId }?.id?.trim()?.takeIf(String::isNotBlank)
                    if (headerId == null) {
                        _uiState.update { state ->
                            if (state.editorTimetableClassSectionId == normalizedId) state.copy(isEditorTimetableLoading = false) else state
                        }
                    } else {
                        TimetableRepository.load(headerId).fold(
                            onSuccess = { details ->
                                _uiState.update { state ->
                                    if (state.editorTimetableClassSectionId == normalizedId) state.copy(
                                        editorTimetableHeaderId = headerId,
                                        editorTimetable = details,
                                        isEditorTimetableLoading = false
                                    ) else state
                                }
                            },
                            onFailure = { error ->
                                _uiState.update { state ->
                                    if (state.editorTimetableClassSectionId == normalizedId) state.copy(
                                        isEditorTimetableLoading = false,
                                        editorTimetableError = error.message ?: "Unable to load this class timetable."
                                    ) else state
                                }
                            }
                        )
                    }
                },
                onFailure = { error ->
                    _uiState.update { state ->
                        if (state.editorTimetableClassSectionId == normalizedId) state.copy(
                            isEditorTimetableLoading = false,
                            editorTimetableError = error.message ?: "Unable to load this class timetable."
                        ) else state
                    }
                }
            )
        }
    }

    private fun loadView(row: TimeTableRow) {
        viewJob?.cancel()
        val headerId = row.headerId?.trim().orEmpty()
        if (headerId.isBlank()) {
            _uiState.update { it.copy(viewDetails = null, isViewLoading = false, viewErrorMessage = "No timetable has been published for this class yet.") }
            return
        }
        viewJob = scope.launch {
            _uiState.update { it.copy(viewDetails = null, isViewLoading = true, viewErrorMessage = "") }
            TimetableRepository.load(headerId).fold(
                onSuccess = { details -> _uiState.update { it.copy(viewDetails = details, isViewLoading = false) } },
                onFailure = { error -> _uiState.update { it.copy(isViewLoading = false, viewErrorMessage = error.message ?: "Unable to load this timetable.") } }
            )
        }
    }

    private fun save(headerId: String?, request: TimetableCombinedCreateRequest, onSuccess: () -> Unit) {
        if (request.classSectionId.isBlank()) return
        _uiState.update { it.copy(editorSaveError = "") }
        scope.launch {
            val result = headerId?.trim()?.takeIf { it.isNotBlank() }?.let { TimetableRepository.update(it, request) }
                ?: TimetableRepository.create(request)
            result.fold(
                onSuccess = { AppToastCenter.showSuccess("Timetable saved successfully"); onSuccess(); load() },
                onFailure = { error ->
                    val message = error.message ?: "Unable to save timetable."
                    _uiState.update { it.copy(editorSaveError = message, errorMessage = message) }
                }
            )
        }
    }

    private fun updateAndLoad(transform: TimeTableUiState.() -> TimeTableUiState) {
        _uiState.update(transform)
        load()
    }

    private fun load() {
        loadJob?.cancel()
        loadJob = scope.launch {
            val state = _uiState.value
            _uiState.update { it.copy(isLoading = true, errorMessage = "") }
            TimetableRepository.loadAll(refreshScope = scope).fold(
                onSuccess = { headers ->
                    val allRows = mergeClassSectionsWithTimetables(state.classSections, headers)
                    val filteredRows = allRows
                        .filter { row ->
                            val query = state.searchQuery.trim()
                            val matchesSearch = query.isBlank() || row.classSection.contains(query, ignoreCase = true)
                            val matchesClass = state.classFilter.isBlank() || row.classSection == state.classFilter
                            val matchesStatus = state.statusFilter.isBlank() || row.status.equals(state.statusFilter, ignoreCase = true)
                            matchesSearch && matchesClass && matchesStatus
                        }
                        .let { rows -> sortRows(rows, state.sortBy, state.sortAscending) }
                    val from = (state.pageIndex * state.pageSize).coerceAtMost(filteredRows.size)
                    _uiState.update {
                        it.copy(
                            isLoading = false,
                            rows = filteredRows.drop(from).take(state.pageSize),
                            totalCount = filteredRows.size
                        )
                    }
                },
                onFailure = { error ->
                    _uiState.update { it.copy(isLoading = false, rows = emptyList(), totalCount = 0, errorMessage = error.message ?: "Unable to load timetables.") }
                }
            )
        }
    }

    fun clear() { loadJob?.cancel(); viewJob?.cancel(); subjectsJob?.cancel(); editorTimetableJob?.cancel(); scope.cancel() }
}

private fun TimetableClassHeaderResponse.toUiRow() = TimeTableRow(
    id = id.orEmpty(),
    headerId = id,
    classSectionId = classSectionId.orEmpty(),
    classSection = listOf(className, sectionName).mapNotNull { it?.trim()?.takeIf(String::isNotBlank) }.joinToString("-").ifBlank { "—" },
    lastUpdated = formatApiTimestamp(updatedAt),
    lastUpdatedBy = updatedByName?.trim()?.takeIf(String::isNotBlank) ?: "—",
    status = status?.trim().orEmpty().ifBlank { TimetableStatusValues.BLANK }
)

private fun mergeClassSectionsWithTimetables(
    classSections: List<TimetableClassSectionOption>,
    headers: List<TimetableClassHeaderResponse>
): List<TimeTableRow> {
    val headersByClassSection = headers
        .filter { !it.classSectionId.isNullOrBlank() }
        .associateBy { it.classSectionId }
    return classSections.map { section ->
        headersByClassSection[section.id]?.toUiRow()
            ?: TimeTableRow(
                id = "class-section:${section.id}",
                classSectionId = section.id,
                classSection = listOf(section.className, section.sectionName)
                    .filter(String::isNotBlank)
                    .joinToString("-")
                    .ifBlank { "—" },
                lastUpdated = "—",
                lastUpdatedBy = "—",
                status = TimetableStatusValues.BLANK
            )
    }
}

private fun sortRows(rows: List<TimeTableRow>, sortBy: String, ascending: Boolean): List<TimeTableRow> {
    val comparator = when (sortBy) {
        "status" -> compareBy<TimeTableRow> { it.status.lowercase() }
        "updated_at" -> compareBy { it.lastUpdated }
        "section_name" -> Comparator { left, right ->
            compareTimetableLabelsNaturally(
                left.classSection.substringAfterLast('-', left.classSection),
                right.classSection.substringAfterLast('-', right.classSection)
            )
        }
        else -> Comparator { left, right ->
            compareTimetableClassSections(
                left.classSection.substringBeforeLast('-', left.classSection),
                left.classSection.substringAfterLast('-', ""),
                right.classSection.substringBeforeLast('-', right.classSection),
                right.classSection.substringAfterLast('-', "")
            )
        }
    }
    return if (ascending) rows.sortedWith(comparator) else rows.sortedWith(comparator.reversed())
}

private fun formatApiDate(value: String?): String {
    val parts = value.orEmpty().substringBefore('T').split('-')
    if (parts.size != 3) return value?.ifBlank { "—" } ?: "—"
    val months = listOf("Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec")
    val month = parts[1].toIntOrNull()?.let { months.getOrNull(it - 1) } ?: return value ?: "—"
    return "${parts[2].padStart(2, '0')} $month ${parts[0]}"
}

private fun formatApiTimestamp(value: String?): String = formatApiDate(value).let { date ->
    val time = value.orEmpty().substringAfter('T', "").take(5)
    if (time.isBlank()) date else "$date $time"
}
